import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Brand } from "@/components/Brand";
import { api, formatErr } from "@/lib/api";

const SECTIONS = [["challenge", "The challenge"], ["approach", "How you use GrantVerify"], ["progress", "Where things stand"]];

export default function CaseStudyApprove() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [changes, setChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  useEffect(() => { api.get(`/public/case-study-approve/${token}`).then((r) => setC(r.data)).catch((e) => setErr(formatErr(e))); }, [token]);
  const decide = (approve) => {
    if (!approve && note.trim().length < 3) return toast.error("Tell us what to change");
    setBusy(true);
    api.post(`/public/case-study-approve/${token}`, { approve, note }).then((r) => setResult(r.data.status)).catch((e) => toast.error(formatErr(e))).finally(() => setBusy(false));
  };
  const status = result || c?.status;
  let body;
  if (err) body = <p className="text-slate-600" data-testid="case-approve-error">{err}</p>;
  else if (!c) body = <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  else body = (
    <div className="space-y-8">
      <div><div className="eyebrow mb-3">Your story, for your approval</div><p className="text-slate-600">Hi {c.name}, here's the draft about {c.business}. It stays private unless you approve it.</p></div>
      <article className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 space-y-5" data-testid="case-approve-draft">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{c.title}</h1>
        {SECTIONS.map(([k, l]) => <div key={k}><h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">{l}</h2><p className="text-slate-700 mt-1">{c[k]}</p></div>)}
        {c.quote && <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-800">“{c.quote}”<div className="not-italic text-sm text-slate-500 mt-1">{c.name}, {c.business}</div></blockquote>}
      </article>
      {status === "published" ? <div className="flex items-center gap-2 text-emerald-700 font-semibold" data-testid="case-approve-published"><Check className="h-5 w-5" />Approved. Thank you. Your story is now on our Customers page.</div>
        : status === "changes_requested" ? <p className="text-slate-700" data-testid="case-approve-changes-sent">Thanks. We'll make your changes and send you a new version.</p>
        : status !== "awaiting_member" ? <p className="text-slate-600" data-testid="case-approve-closed">This version is no longer waiting for review.</p>
        : <div className="space-y-3">
          {changes && <Textarea rows={3} maxLength={1500} placeholder="What should we change or remove?" value={note} onChange={(e) => setNote(e.target.value)} aria-label="What should we change" data-testid="case-approve-note" />}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => decide(true)} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="case-approve-yes">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Approve and publish</Button>
            {changes ? <Button variant="outline" onClick={() => decide(false)} disabled={busy} className="rounded-full" data-testid="case-approve-send-changes">Send changes</Button>
              : <Button variant="outline" onClick={() => setChanges(true)} className="rounded-full" data-testid="case-approve-request-changes">Request changes</Button>}
          </div>
        </div>}
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-approve-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center"><Brand /></div></header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16">{body}</main>
    </div>
  );
}
